import { apiSlice } from "./apiSlice";

const ALL_CITIES_CACHE_KEY_BASE = "mapitShipApi:getAllCities";
const ALL_CITIES_CACHE_TTL_MS = 60 * 1000; // 3 hours in milliseconds

const canUseLocalStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const getAllCitiesCacheKey = () => ALL_CITIES_CACHE_KEY_BASE;

const readAllCitiesCache = () => {
  if (!canUseLocalStorage()) {
    return null;
  }

  const storage = window.localStorage;
  const cacheKey = getAllCitiesCacheKey();

  try {
    const cachedValue = storage.getItem(cacheKey);
    if (!cachedValue) {
      return null;
    }

    const payload = JSON.parse(cachedValue);
    if (!payload || typeof payload.timestamp !== "number") {
      storage.removeItem(cacheKey);
      return null;
    }

    const isExpired = Date.now() - payload.timestamp > ALL_CITIES_CACHE_TTL_MS;
    if (isExpired) {
      storage.removeItem(cacheKey);
      return null;
    }

    return payload.data ?? null;
  } catch {
    try {
      storage.removeItem(cacheKey);
    } catch {
      // ignore cleanup failures
    }
    return null;
  }
};

const writeAllCitiesCache = (data) => {
  if (!canUseLocalStorage()) {
    return;
  }

  const storage = window.localStorage;
  const cacheKey = getAllCitiesCacheKey();

  try {
    storage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data }));
  } catch {
    // ignore storage quota/privacy errors
  }
};

export const mapitShipApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      "ADMIN-MAPIT-SHIP-ORDER",
      "MAPIT-SHIP-ORDER",
      "WALLET",
      "CITIES",
      "OTO-CITIES",
      "ARAMEX-SATCHEL",
      "SHIPING",
      "ORDERS-VALIDATION"
    ],
  })
  .injectEndpoints({
    keepUnusedDataFor: 1,
    endpoints: (builder) => ({
      //======= Wallet Endpoints =========
      getWalletBalance: builder.query({
        query: () => ({
          url: `/wallet/balance`,
          method: "GET",
        }),
        providesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getMerchantsWalletBalance: builder.query({
        query: (params) => ({
          url: `/wallet/merchant/balance`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getWalletTransactions: builder.query({
        query: (params) => ({
          url: `/wallet/transaction`,
          method: "GET",
          params,
        }),
        providesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getWalletTransactionsByAdmin: builder.query({
        query: (params) => ({
          url: `/wallet/transaction/admin`,
          method: "GET",
          params,
        }),
        providesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getMapitShipStatisticsByAdmin: builder.query({
        // type = [profit, wallet-charge, cod, wallet-balance, shipments]
        query: ({ type, ...params }) => ({
          url: `/wallet/transaction/admin/${type}/stats`,
          method: "GET",
          params,
        }),
        providesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getWalletTransactionsXls: builder.query({
        query: (params) => ({
          url: `/wallet/transaction/xls`,
          method: "GET",
          params,
        }),
        providesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getWalletTransactionsXlsByAdmin: builder.query({
        query: (params) => ({
          url: `/wallet/transaction/xls/admin`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      chargeWallet: builder.mutation({
        query: (payload) => ({
          url: `/wallet/charge`,
          method: "POST",
          body: { ...payload },
        }),
        invalidatesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      transactionWallet: builder.mutation({
        query: (payload) => ({
          url: `/transaction/wallet`,
          method: "POST",
          body: { ...payload },
        }),
        invalidatesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      chargeWalletByAdmin: builder.mutation({
        query: (payload) => ({
          url: `/wallet/transaction`,
          method: "POST",
          body: { ...payload },
        }),
        invalidatesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      updateTransactionToRefund: builder.mutation({
        query: (payload) => ({
          url: `/wallet/transaction`,
          method: "PUT",
          body: { ...payload },
        }),
        invalidatesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getTransactionById: builder.query({
        query: (transactionId) => ({
          url: `/wallet/transaction-details/${transactionId}`,
          method: "GET",
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      //======= Charge OTO Endpoints =========
      chargeOTOWallet: builder.mutation({
        query: (payload) => ({
          url: `/oto/charge`,
          method: "POST",
          body: { ...payload },
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      //======= OTO Create Order Endpoints =========
      getMapitShipOrders: builder.query({
        query: (params) => ({
          url: `/oto/order`,
          method: "GET",
          params,
        }),
        providesTags: (result, _, __) =>
          result
            ? [
              ...result?.docs.map(({ id }) => ({
                type: "MAPIT-SHIP-ORDER",
                id,
              })),
              {
                type: "MAPIT-SHIP-ORDER",
                id: "MAPIT-SHIP-ORDER-PARTIAL-LIST",
              },
            ]
            : [
              {
                type: "MAPIT-SHIP-ORDER",
                id: "MAPIT-SHIP-ORDER-PARTIAL-LIST",
              },
            ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getMapitShipOrdersById: builder.query({
        query: (id) => ({
          url: `/oto/order/${id}`,
          method: "GET",
        }),
        providesTags: (_, __, id) => [{ type: "MAPIT-SHIP-ORDER", id }],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getMapitShipOrderAWB: builder.query({
        query: (orderId) => ({
          url: `/shipment/order/${orderId}/awb`,
          method: "GET",
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      createMapitShipOrder: builder.mutation({
        query: (payload) => ({
          url: `/oto/order`,
          method: "POST",
          body: { ...payload },
        }),
        invalidatesTags: [
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) => ({
          status: response?.data?.status ?? response?.status,
          message:
            response?.data?.message ?? "Something went wrong please try again",
        }),
      }),
      importMapitShipOrdersXls: builder.mutation({
        query: (payload) => ({
          url: `/oto/order/xls`,
          method: "POST",
          body: { ...payload },
        }),
        invalidatesTags: [
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      updateMapitShipOrder: builder.mutation({
        query: ({ id, payload }) => ({
          url: `/oto/order/${id}`,
          method: "PUT",
          body: { ...payload },
        }),
        invalidatesTags: (_, __, { id }) => [
          { type: "MAPIT-SHIP-ORDER", id },
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      cancelMapitShipOrder: builder.mutation({
        query: (id) => ({
          url: `/oto/order/${id}/cancel`,
          method: "PUT",
        }),
        invalidatesTags: (_, __, { id }) => [
          { type: "MAPIT-SHIP-ORDER", id },
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
          { type: "ADMIN-MAPIT-SHIP-ORDER", id },
          {
            type: "ADMIN-MAPIT-SHIP-ORDER",
            id: "ADMIN-MAPIT-SHIP-ORDER-PARTIAL-LIST",
          },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      cancelMapitShipShipment: builder.mutation({
        query: (payload) => ({
          url: `/shipment/cancel`,
          method: "PUT",
          body: { ...payload },
        }),
        invalidatesTags: (_, __, { id }) => [
          { type: "MAPIT-SHIP-ORDER", id },
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
          { type: "ADMIN-MAPIT-SHIP-ORDER", id },
          {
            type: "ADMIN-MAPIT-SHIP-ORDER",
            id: "ADMIN-MAPIT-SHIP-ORDER-PARTIAL-LIST",
          },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      returnMapitShipOrder: builder.mutation({
        query: (payload) => ({
          url: `/shipment/${payload?.deliveryOption?.integrationType}/return`,
          method: "PUT",
          body: { ...payload },
        }),
        invalidatesTags: (_, __, { orderId }) => [
          { type: "MAPIT-SHIP-ORDER", id: orderId },
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      //======= OTO Get Delivery Fee for Order Endpoints =========
      getDeliveryCompanies: builder.query({
        query: ({ orderId, params }) => ({
          url: `/oto/order/${orderId}/delivery-fee`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      //======= Assign Delivery company to orders for shipment Endpoints =========
      assignDeliveryComapny: builder.mutation({
        query: (payload) => ({
          url: `/shipment`,
          method: "POST",
          body: { ...payload },
        }),
        invalidatesTags: (_, __, payload) => [
          { type: "MAPIT-SHIP-ORDER", id: payload?.orderId },
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      // Same endpoint as `assignDeliveryComapny`, but without `invalidatesTags`.
      // The bulk-assign runner fires this once per order; letting each success
      // invalidate the list would refetch the orders grid dozens of times during
      // a single batch. It invalidates once itself when the batch ends.
      assignDeliveryCompanySilent: builder.mutation({
        query: (payload) => ({
          url: `/shipment`,
          method: "POST",
          body: { ...payload },
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getWalletChargingStatus: builder.query({
        query: (transactionNumber) => ({
          url: `/wallet/transaction/${transactionNumber}`,
          method: "GET",
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getMapitShipOrdersByAdmin: builder.query({
        query: (params) => ({
          url: `/oto/order/admin`,
          method: "GET",
          params,
        }),
        providesTags: (result, _, __) =>
          result
            ? [
              ...result?.docs.map(({ id }) => ({
                type: "ADMIN-MAPIT-SHIP-ORDER",
                id,
              })),
              {
                type: "ADMIN-MAPIT-SHIP-ORDER",
                id: "ADMIN-MAPIT-SHIP-ORDER-PARTIAL-LIST",
              },
            ]
            : [
              {
                type: "ADMIN-MAPIT-SHIP-ORDER",
                id: "ADMIN-MAPIT-SHIP-ORDER-PARTIAL-LIST",
              },
            ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      checkShippingCompanyAcceptOrder: builder.mutation({
        query: (orderId) => ({
          url: `/shipment/order/${orderId}/check`,
          method: "PUT",
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
        invalidatesTags: [
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
      }),
      calculateShipmentPrice: builder.mutation({
        query: (payload) => ({
          url: `/oto/price-calculator`,
          method: "POST",
          body: { ...payload },
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      reOrderCancelShipment: builder.mutation({
        query: (payload) => ({
          url: `/oto/re-order`,
          method: "POST",
          body: { ...payload },
        }),
        invalidatesTags: [
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      uploadOtoTransactions: builder.mutation({
        query: (payload) => ({
          url: `/oto/transactions`,
          method: "POST",
          body: { ...payload },
        }),
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      //======= Orders Count Endpoints =========
      getMapitShipOrdersCountByAdmin: builder.query({
        query: (params) => ({
          url: `/oto/order/admin/count`,
          method: "GET",
          params,
        }),
        providesTags: ["ADMIN-MAPIT-SHIP-ORDER", "MAPIT-SHIP-ORDER"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getMapitShipOrdersCount: builder.query({
        query: (params) => ({
          url: `/oto/order/count`,
          method: "GET",
          params,
        }),
        providesTags: ["ADMIN-MAPIT-SHIP-ORDER", "MAPIT-SHIP-ORDER"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      downloadAwbForShipments: builder.mutation({
        query: (payload) => ({
          url: `/oto/orders/awb`,
          method: "POST",
          body: { ...payload },
          responseHandler: (response) => response.blob(), // Add this line
        }),
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getMapitShipOrdersByIds: builder.query({
        query: (params) => ({
          url: `/oto/order/details`,
          method: "GET",
          params,
        }),
        providesTags: (_, __, { ids }) =>
          ids?.length
            ? [
              ...ids.map((id) => ({ type: "MAPIT-SHIP-ORDER", id })),
              {
                type: "MAPIT-SHIP-ORDER",
                id: "MAPIT-SHIP-ORDER-PARTIAL-LIST",
              },
            ]
            : [
              {
                type: "MAPIT-SHIP-ORDER",
                id: "MAPIT-SHIP-ORDER-PARTIAL-LIST",
              },
            ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      validateWarehousesForOrders: builder.query({
        query: ({ ids, isAdmin }) => ({
          url: `/oto/order/validate-warehouse/${isAdmin ? "admin" : ""}`,
          method: "GET",
          params: { ids },
        }),
        // providesTags: ["ORDERS-VALIDATION"],
        transformResponse: (response) => response?.body,
        transformErrorResponse: (response) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      getMapitshipAccountStats: builder.query({
        query: (params) => ({
          url: `/oto/order/statistics`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      sendEmailToAramex: builder.mutation({
        query: (payload) => ({
          url: `/shipment/pickup-request-email`,
          method: "POST",
          body: { ...payload },
          transformResponse: (response, meta, arg) => response?.body,
          transformErrorResponse: (response, meta, arg) =>
            response?.data?.message ?? "Something went wrong please try again",
        }),
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getAllCities: builder.query({
        async queryFn(_, api, extraOptions, baseQuery) {
          const forceRefetch = Boolean(extraOptions?.forceRefetch);
          const cachedData = forceRefetch ? null : readAllCitiesCache();

          if (cachedData) {
            return { data: cachedData };
          }

          const result = await baseQuery(
            {
              url: `/city/all`,
              method: "GET",
            },
            api,
            extraOptions,
          );

          if (result.error) {
            return { error: result.error };
          }

          const responseData = result.data?.body ?? result.data;
          writeAllCitiesCache(responseData);

          return { data: responseData };
        },
        keepUnusedDataFor: 604800,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getAdminMerchantPerMonthStats: builder.query({
        query: (params) => ({
          url: `/shipment/merchant-per-month-stats`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      //======= Mapitship Cities Endpoints =========
      getCities: builder.query({
        query: (params) => {
          return {
            url: `/city`,
            method: "GET",
            params: params,
          };
        },
        providesTags: ["CITIES"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      createMapitshipCities: builder.mutation({
        query: (body) => ({
          url: `/city`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["CITIES"],
        transformResponse: (response, meta, arg) => {
          console.log("Create response:", response);
          return response?.body ?? response;
        },

        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      updateMapitShipCities: builder.mutation({
        query: ({ payload, id }) => ({
          url: `/city/${id}`,
          method: "PATCH",
          body: { ...payload },
        }),
        invalidatesTags: ["CITIES"],
        transformResponse: (response, meta, arg) => {
          console.log("Create response:", response);
          return response?.body ?? response;
        },

        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      deleteMapitShipCities: builder.mutation({
        query: (id) => ({
          url: `/city/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["CITIES"],

        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      //////////////////// OTO Cities Endpoints ///////////////////
      getOTOCities: builder.query({
        query: (params) => {
          return {
            url: `oto/city`,
            method: "GET",
            params: params,
          };
        },
        providesTags: ["OTO-CITIES"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      createOTOCities: builder.mutation({
        query: (body) => ({
          url: `/oto/city`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["OTO-CITIES"],
        transformResponse: (response, meta, arg) => {
          console.log("Create response:", response);
          return response?.body ?? response;
        },

        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      updateOTOCities: builder.mutation({
        query: ({ payload, id }) => ({
          url: `/oto/city/${id}`,
          method: "PATCH",
          body: { ...payload },
        }),
        invalidatesTags: ["OTO-CITIES"],
        transformResponse: (response, meta, arg) => {
          console.log("Create response:", response);
          return response?.body ?? response;
        },

        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      deleteOTOCities: builder.mutation({
        query: (id) => ({
          url: `/oto/city/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["OTO-CITIES"],

        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      ///////////// top merchant admin dashboard/////////////
      getAdminTopMerchent: builder.query({
        query: (params) => ({
          url: `/admin-panel/merchant/top-merchants`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      getMerchantsStoppedShipping: builder.query({
        query: (params) => ({
          url: `/shipment/merchants/stopped-shipping`,
          method: "GET",
          params,
        }),
        providesTags: ["SHIPING"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      //////////////////////////////Merchant Shipment Chart /////////////////
      getMerchantShipmentChart: builder.query({
        query: ({ id, fromDate, toDate }) => {
          return {
            url: `/admin-panel/merchant/${id}/chart`,
            params: { fromDate, toDate },
          };
        },

        transformResponse: (response, meta, arg) => {
          // console.log("ARG (params):", arg);
          // console.log("REQUEST URL:", meta?.request?.url);

          return response?.body;
        },
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      ///////////////////////////// Return MapitShip Account Admin /////////////////////

      returnMapitShipOrderSAdmin: builder.mutation({
        query: (payload) => ({
          url: `/shipment/${payload?.deliveryOption?.integrationType}/return/admin`,
          method: "PUT",
          body: { ...payload },
        }),
        invalidatesTags: (_, __, { orderId }) => [
          { type: "MAPIT-SHIP-ORDER", id: orderId },
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      //////////////////////////////// Create shipment Account Admin //////////
      createMapiShipOrderAdmin: builder.mutation({
        query: (body) => ({
          url: `/shipment/admin`,
          method: "POST",
          body,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      /////////////////////////// Get Company shipment Admin  ////////////////////////

      getDeliveryCompaniesAdmin: builder.query({
        query: ({ id, params }) => ({
          url: `/oto/order/${id}/delivery-fee/admin`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      //////////////// Get Company By Id Admin////////////////

      getMapitShipOrdersAdminByIds: builder.query({
        query: (params) => ({
          url: `/oto/admin/order/details`,
          method: "GET",
          params,
        }),
        providesTags: (_, __, { ids }) =>
          ids?.length
            ? [
              ...ids.map((id) => ({ type: "MAPIT-SHIP-ORDER", id })),
              {
                type: "MAPIT-SHIP-ORDER",
                id: "MAPIT-SHIP-ORDER-PARTIAL-LIST",
              },
            ]
            : [
              {
                type: "MAPIT-SHIP-ORDER",
                id: "MAPIT-SHIP-ORDER-PARTIAL-LIST",
              },
            ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      updateMapitShipOrderAdmin: builder.mutation({
        query: ({ id, payload, password }) => ({
          url: `/oto/admin/order/${id}`,
          method: "PUT",
          body: { ...payload, password },
        }),
        invalidatesTags: (_, __, { id }) => [
          { type: "MAPIT-SHIP-ORDER", id },
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
          { type: "ADMIN-MAPIT-SHIP-ORDER", id },
          { type: "ADMIN-MAPIT-SHIP-ORDER", id: "ADMIN-MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),

      getOtoTransactionsDiscrepancies: builder.query({
        query: (params) => ({
          url: `/oto/order/discrepancies/admin`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getExcelFileMapitShipForAdmin: builder.query({
        query: (params) => ({
          url: `/oto/order/admin/xls`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getExcelFileMapitShip: builder.query({
        query: (params) => ({
          url: `/oto/order/export-xls`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getWeightTransactionsExistence: builder.query({
        query: (params) => ({
          url: `/wallet/shipment/weight-transactions-existence`,
          method: "GET",
          params,
        }),
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      getHiddenWalletTransactionsByAdmin: builder.query({
        query: (params) => ({
          url: `/wallet/transaction/hidden`,
          method: "GET",
          params,
        }),
        providesTags: ["WALLET"],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      cloneMapitShipOrders: builder.mutation({
        query: (payload) => ({
          url: `/oto/order/clone`,
          method: "POST",
          body: { ...payload },
        }),
        invalidatesTags: [
          { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
        ],
        transformResponse: (response, meta, arg) => response?.body,
        transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
      }),
      showShipmentList: builder.query({
          query: (params) => ({
            url: `shipment`,
            method: "GET",
            params,
          }),
           transformResponse: (response, meta, arg) => response?.body,
          transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
        }),
      exportFileShipments: builder.query({
          query: (params) => ({
            url: `/shipment/export-xls`,
            method: "GET",
            params,
          }),
           transformResponse: (response, meta, arg) => response?.body,
          transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
        }),
      forceCancelShipment: builder.mutation({
          query: (payload) => ({
            url: `/shipment/force-cancel`,
            method: "PUT",
             body: { ...payload },
          }),
           transformResponse: (response, meta, arg) => response?.body,
          transformErrorResponse: (response, meta, arg) =>
          response?.data?.message ?? "Something went wrong please try again",
        })
    }),
  });

export const {
  useGetWalletBalanceQuery,
  useLazyGetWalletBalanceQuery,
  useGetWalletTransactionsQuery,
  useLazyGetWalletTransactionsQuery,
  useChargeWalletMutation,
  useGetCitiesQuery,
  useLazyGetCitiesQuery,
  useChargeOTOWalletMutation,
  useCreateMapitShipOrderMutation,
  useUpdateMapitShipOrderMutation,
  useGetMapitShipOrdersQuery,
  useLazyGetMapitShipOrdersQuery,
  useGetDeliveryCompaniesQuery,
  useLazyGetDeliveryCompaniesQuery,
  useAssignDeliveryComapnyMutation,
  useAssignDeliveryCompanySilentMutation,
  useGetMapitShipOrdersByIdQuery,
  useLazyGetMapitShipOrdersByIdQuery,
  useCancelMapitShipOrderMutation,
  useLazyGetMapitShipOrderAWBQuery,
  useLazyGetWalletTransactionsXlsQuery,
  useLazyGetWalletTransactionsXlsByAdminQuery,
  useImportMapitShipOrdersXlsMutation,
  useGetWalletTransactionsByAdminQuery,
  useLazyGetWalletTransactionsByAdminQuery,
  useChargeWalletByAdminMutation,
  useReturnMapitShipOrderMutation,
  useLazyGetWalletChargingStatusQuery,
  useCancelMapitShipShipmentMutation,
  useGetMapitShipOrdersByAdminQuery,
  useLazyGetMapitShipOrdersByAdminQuery,
  useCheckShippingCompanyAcceptOrderMutation,
  useTransactionWalletMutation,
  useGetMapitShipStatisticsByAdminQuery,
  useCalculateShipmentPriceMutation,
  useGetMerchantsWalletBalanceQuery,
  useReOrderCancelShipmentMutation,
  useLazyGetTransactionByIdQuery,
  useUploadOtoTransactionsMutation,
  useUpdateTransactionToRefundMutation,
  useGetMapitShipOrdersCountByAdminQuery,
  useGetMapitShipOrdersCountQuery,
  useDownloadAwbForShipmentsMutation,
  useGetMapitShipOrdersByIdsQuery,
  useLazyValidateWarehousesForOrdersQuery,
  useGetOTOCitiesQuery,
  useLazyGetOTOCitiesQuery,
  useCreateOTOCitiesMutation,
  useUpdateOTOCitiesMutation,
  useDeleteOTOCitiesMutation,
  useGetMapitshipAccountStatsQuery,
  useSendEmailToAramexMutation,
  useGetAllCitiesQuery,
  useLazyGetAllCitiesQuery,
  useGetAdminMerchantPerMonthStatsQuery,
  useLazyGetAdminMerchantPerMonthStatsQuery,
  useGetAdminTopMerchentQuery,
  useGetMerchantsStoppedShippingQuery,
  useCreateMapitshipCitiesMutation,
  useUpdateMapitShipCitiesMutation,
  useDeleteMapitShipCitiesMutation,
  useLazyGetMerchantShipmentChartQuery,
  useReturnMapitShipOrderSAdminMutation,
  useLazyGetDeliveryCompaniesAdminQuery,
  useGetDeliveryCompaniesAdminQuery,
  useGetMapitShipOrdersAdminByIdsQuery,
  useCreateMapiShipOrderAdminMutation,
  useUpdateMapitShipOrderAdminMutation,
  useLazyGetOtoTransactionsDiscrepanciesQuery,
  useGetExcelFileMapitShipForAdminQuery,
  useLazyGetExcelFileMapitShipForAdminQuery,
  useGetWeightTransactionsExistenceQuery,
  useLazyGetWeightTransactionsExistenceQuery,
  useLazyGetExcelFileMapitShipQuery,
  useLazyGetHiddenWalletTransactionsByAdminQuery,
  useCloneMapitShipOrdersMutation,
  useShowShipmentListQuery,
  useLazyExportFileShipmentsQuery,
  useForceCancelShipmentMutation
} = mapitShipApi;
